import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, message, Tooltip, Spin, Badge, Tag, Select,Row } from 'antd';
import { EditOutlined, DeleteOutlined, ExclamationCircleOutlined, CheckOutlined, UserAddOutlined } from '@ant-design/icons';
import {
  createUserService,
  updateUserService,
  deleteUserService,

  fetchUsersService,
  registerUser,
  approveUserService,
} from '../../services/UserService';
import CustomLoadingModal from '../Modal/CustomLoadingModal';
import { useNavigate } from 'react-router-dom'; // To navigate to the detail page
import UserTableFilter from '../Filters/UserTableFilter';

const { confirm } = Modal;

const UserTable = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [form] = Form.useForm();

   const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [modalMessage, setModalMessage] = useState('Creation of user in progress...');
    const [loading, setLoading] = useState(false);

     const navigate = useNavigate();

  // New state for filters
  const [filters, setFilters] = useState({
    nameSearch: '',
    emailSearch: '',
    roleFilter: [],
    statusFilter: []
  });

        const handleReadMore = (article) => {
            navigate(`/admin/articles/${article.id}/view`, { state: { article } }); // Pass the article object via state
            console.log(`Redirect to article with ID: ${article.id}`);
          };

    const handleApprove = async (userId) => {
      setLoading(true);
      try {
        const approvedUser = await approveUserService(userId);
        message.success(`User with ID: ${approvedUser.id} approved successfully!`);
        setTableLoading(true);
        fetchUsersService()
          .then(data => {
            setUsers(data);
          })
          .catch(error => {
            message.error('Error fetching categories');
          })
          .finally(() => {
            setTableLoading(false);
          });
      } catch (error) {
        message.error('Error approving user');
      } finally {
        setLoading(false);
        
      }
    };
  

    const fetchUsers = useCallback(() => {
      setTableLoading(true);
      fetchUsersService()
        .then(data => {
          setUsers(data);
          applyFilters(data, filters); // Apply existing filters to the new data
        })
        .catch(error => {
          message.error('Error fetching users');
        })
        .finally(() => {
          setTableLoading(false);
        });
    }, [filters]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

   // Apply filters to the user data
   const applyFilters = (userList, currentFilters) => {
    let result = [...userList];
    
    // Filter by name
    if (currentFilters.nameSearch) {
      result = result.filter(user => 
        user.name.toLowerCase().includes(currentFilters.nameSearch.toLowerCase())
      );
    }
    
    // Filter by email
    if (currentFilters.emailSearch) {
      result = result.filter(user => 
        user.email.toLowerCase().includes(currentFilters.emailSearch.toLowerCase())
      );
    }
    
    // Filter by role
    if (currentFilters.roleFilter && currentFilters.roleFilter.length > 0) {
      result = result.filter(user => 
        currentFilters.roleFilter.includes(user.roleType)
      );
    }
    
    // Filter by status
    if (currentFilters.statusFilter && currentFilters.statusFilter.length > 0) {
      result = result.filter(user => 
        currentFilters.statusFilter.includes(user.userStatus)
      );
    }
    
    setFilteredUsers(result);
  };

  // Handle filter changes from the filter component
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    applyFilters(users, newFilters);
  };


  const showDeleteConfirm = (id) => {
    confirm({
      title: 'Are you sure you want to delete this user?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: () => {
        deleteUserService(id)
          .then(() => {
            setUsers(prevUsers => prevUsers.filter(cat => cat.id !== id));
            message.success('User deleted successfully');
          })
          .catch(error => {
            message.error('Error deleting category');
          });
      },
      onCancel() {
        console.log('Delete canceled');
      },
    });
  };

  const handleSubmit = async (values) =>  {
    setFormLoading(true);
    const { name, parentId } = values;
    setIsCreateModalVisible(true);
        setIsSubmitting(true)

    if (editingUser) {
     updateUserService(editingUser.id, values)
        .then(updatedUser => {
         
          message.success('User updated successfully');
          setIsModalVisible(false);
          form.resetFields();
          fetchUsers();
        })
        .catch(error => {
          message.error('Error updating category');
        })
        .finally(() => {
          setFormLoading(false);
        });
    } else {
      try {
        
        const userResponse = await registerUser(values);
        console.log('Registered user:', userResponse);
        setFormLoading(false);
        setIsCreateModalVisible(false);
        setIsSubmitting(false);
        message.success("User created successfully!");
        form.resetFields();
        fetchUsers();
      } catch (error) {
        console.error('User registration failed', error);
        setFormLoading(false);
        setIsCreateModalVisible(false);
        setIsSubmitting(false)
      }
      
    }
  };

  const showEditModal = (category) => {
    setEditingUser(category);
    form.setFieldsValue({ name: category.name, email: category.email || null, roleType: category.roleType || null, userStatus: category.userStatus || null });
    setIsModalVisible(true);
  };

  const showCreateModal = () => {
    setEditingUser(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const columns = [
    {
      title: 'Role',
      dataIndex: 'roleType',
      key: 'roleType',
      render: (role) => {
        const colorMap = { 
          'ADMIN': '#108ee9', 
          'EDITOR': '#87d068', 
          'READER': '#2db7f5' 
        };
        return <Tag color={colorMap[role]}>{role}</Tag>;
      }
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    
    {
      title: 'Status',
      dataIndex: 'userStatus',
      key: 'userStatus',
      render: (status) => (
        <Tag
          style={{
            border: `1px solid ${status.toUpperCase() === 'ACTIVE' ? '#52c41a' : '#ff4d4f'}`,
            color: status.toUpperCase() === 'ACTIVE' ? '#52c41a' : '#ff4d4f',
            backgroundColor: 'white',
            borderRadius: '6px',
            padding: '2px 10px',
          }}
        >
          {status.toUpperCase() === 'ACTIVE' ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => new Date(text).toLocaleString(),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text, record) => (
        <>
         <Row justify="start" align="start">
         <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            onClick={() => showDeleteConfirm(record.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: 8,
              marginRight: 8,
            }}
          >
           Delete
          </Button>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => showEditModal(record)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: 8,
              marginRight: 8,
            }}
          >
           Edit
          </Button>
          {record.userStatus.toUpperCase() === 'INACTIVE' && (
           
            <Button
              type="primary"
              icon={<CheckOutlined />}
              loading={loading}
              onClick={() => handleApprove(record.id)}
              disabled={loading}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'white',
                color: 'green',
                border: '1px solid green',
              }}
            >
              Approve
            </Button>
          )}
          
          </Row>
        </>
      ),
    },
  ];

  return (
    <>
    {/* User Filter Component */}
    <UserTableFilter onApplyFilters={handleFilterChange} />
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Button 
          type="primary" 
          onClick={showCreateModal} 
          icon={<UserAddOutlined />}
           size='large'
        >
          Create User
        </Button>
        
        <div>
          {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} found
        </div>
      </div>


      <Spin spinning={tableLoading}>
        <Table 
          columns={columns} 
          dataSource={filteredUsers} 
          rowKey="id" 
          pagination={{ 
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`
          }}
        />
      </Spin>

      {/* Loading Modal */}
      <CustomLoadingModal
        visible={isModalVisible}
        loading={isSubmitting}
        message={modalMessage}
      />
      <Modal
        title={editingUser ? 'Edit User' : 'Create User'}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={formLoading}
      >
        <Form
          form={form}
          onFinish={handleSubmit}
          labelCol={{ span: 24 }} // Ensures the label takes the full width
          wrapperCol={{ span: 24 }} // Ensures the input takes the full width
          layout="vertical" // Moves labels to the top of inputs
        >
          <Form.Item
            name="name"
            label={<span style={{ textAlign: 'center', display: 'block' }}>User Name</span>}
            rules={[{ required: true, message: 'Please input the user name!' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label={<span style={{ textAlign: 'center', display: 'block' }}>Email</span>}
            rules={[
              { required: true, message: 'Please input the email!' },
              { type: 'email', message: 'Please enter a valid email!' },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="roleType"
            label={<span style={{ textAlign: 'center', display: 'block' }}>Role</span>}
            rules={[{ required: true, message: 'Please select the role!' }]}
          >
            <Select>
              <Select.Option value="ADMIN">Admin</Select.Option>
              <Select.Option value="EDITOR">EDITOR</Select.Option>
              <Select.Option value="READER">READER</Select.Option>
            </Select>
          </Form.Item>

          {!editingUser && (
            <Form.Item
              name="password"
              label={<span style={{ textAlign: 'center', display: 'block' }}>Password</span>}
              rules={[{ required: true, message: 'Please input the password!' }]}
            >
              <Input />
            </Form.Item>
          )}

          <Form.Item
            name="userStatus"
            label={<span style={{ textAlign: 'center', display: 'block' }}>Status</span>}
            rules={[{ required: true, message: 'Please select the status!' }]}
          >
            <Select>
              <Select.Option value="ACTIVE">Active</Select.Option>
              <Select.Option value="INACTIVE">Inactive</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ width: '100%' }}>
              Submit
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default UserTable;